import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';

const PHRASE = 'CONFIRMER';

function quantity(armed, preview, percent) {
  if (armed !== undefined && armed !== null) return `${armed} contrats armés (cible ${percent} %)`;
  if (preview !== undefined && preview !== null) return `${preview} contrats prévisionnels (${percent} %)`;
  return `indisponible (${percent} %)`;
}

function Row({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" textAlign="right">{value}</Typography>
    </Box>
  );
}

export default function BlofinLiveConfirmationDialog({
  open, intent, accountRef, dispatchEnabled, busy, error, onClose, onConfirm,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [phrase, setPhrase] = useState('');

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setPhrase('');
    }
  }, [open, intent?.intentId]);

  if (!intent) return null;
  const phraseMatches = phrase.trim().toUpperCase() === PHRASE;
  const intentRecorded = intent.status === 'INTENT_RECORDED';
  const canConfirm = dispatchEnabled && intentRecorded && acknowledged && phraseMatches && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Confirmation explicite — ordre LIVE</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            Cette confirmation concerne un ordre MARKET réel. Le prix de fill peut différer du prix de
            référence. Un SL n’est considéré comme protecteur qu’après relecture distante.
          </Alert>

          <Stack spacing={0.5}>
            <Row label="Compte" value={accountRef} />
            <Row label="Intention" value={intent.intentId} />
            <Row label="Instrument" value={intent.instrumentId} />
            <Row label="Direction" value={intent.direction} />
            <Row label="Type" value="MARKET" />
            <Row label="Marge / levier" value={`${intent.marginUsdt} USDT / x${intent.leverage}`} />
            <Row label="Quantité demandée" value={`${intent.requestedContracts} contrats`} />
            <Row label="Prix de référence" value={intent.entryPrice} />
            <Row label="Stop-loss initial" value={intent.stopLoss} />
            <Row label="TP1" value={`${intent.tp1Price ?? '—'} — ${quantity(intent.tp1Contracts, intent.tp1PreviewContracts, 40)}`} />
            <Row label="TP2" value={`${intent.tp2Price ?? '—'} — ${quantity(intent.tp2Contracts, intent.tp2PreviewContracts, 30)}`} />
            <Row label="TP3" value={`${intent.tp3Price ?? '—'} — ${quantity(intent.tp3Contracts, intent.tp3PreviewContracts, 20)}`} />
            <Row label="Runner" value={intent.runnerContracts != null
              ? `${intent.runnerContracts} contrats armés (cible 10 %)`
              : (intent.runnerPreviewContracts != null
                ? `${intent.runnerPreviewContracts} contrats prévisionnels (10 %)`
                : 'indisponible (10 %)')} />
          </Stack>

          <Divider />

          <Alert severity="info">
            Break-even brut : après TP1, le SL est déplacé au prix moyen d’entrée sans compensation des
            frais. Les frais et le slippage peuvent produire une perte nette.
          </Alert>
          <Alert severity="error">
            Si le SL initial n’est pas confirmé par relecture, la position reste UNPROTECTED et aucun
            TP ne doit être considéré comme armé. Aucune clôture automatique n’est effectuée.
          </Alert>

          {!dispatchEnabled && (
            <Alert severity="warning">
              Dispatch LIVE verrouillé côté serveur. La confirmation finale est impossible dans cet
              environnement. Aucun ordre ne sera envoyé.
            </Alert>
          )}
          {!intentRecorded && (
            <Alert severity="warning">
              Cette intention n’est plus au statut INTENT_RECORDED. La confirmation est refusée.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <FormControlLabel
            control={<Checkbox checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)} />}
            label="Je comprends le risque MARKET, le risque de slippage et le risque d’un SL non confirmé."
          />
          <TextField
            label={`Saisissez ${PHRASE} pour confirmer`}
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            fullWidth
            autoComplete="off"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Annuler</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!canConfirm}
          onClick={() => {
            if (canConfirm) onConfirm();
          }}
        >
          {busy ? <CircularProgress size={18} /> : 'Confirmer l’exécution LIVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
